"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import Logo from "../../assets/SalonTym_Logo.png";
import {
  Box,
  Button,
  Grid,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DropDown from "./DropDown";
import LocationDialog from "../location/LocatioDialog";
import Login from "../login/Login";
import { usePathname, useRouter } from "next/navigation";
import { Address } from "../../classes/main";
import SearchBarMobileView from "../mobile-componenet/SearchBar";
import toast from "react-hot-toast";
import { Loader } from "../genericComponents/MainLoader/Loader";
import { SearchBar } from "./SearchBar";
import { CartIcon } from "./CartIcon";
import Cookies from "js-cookie";
import { isHomeHubPath } from "./homeHubPath";

const LoginContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const LoginButton = styled(Button)`
  text-transform: capitalize;
  box-shadow: none;
`;

interface NavbarProps {
  setReloadCartService: any;
  reloadScrollFlag: any;
  reloadSearchPositionFlag: any;
  reloadNavBackground: any;
  visible: any;
}

const Navbar: React.FC<NavbarProps> = ({
  setReloadCartService,
  reloadScrollFlag,
  reloadSearchPositionFlag: _reloadSearchPositionFlag,
  reloadNavBackground,
  visible,
}) => {
  const [showLoginBox, setShowLoginBox] = useState(false);
  const [alreadyLogin, setAlreadyLogin] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState(new Address());
  const [updateAddress, setUpdateAddress] = useState(false);
  const [reloadLocationFlag, setReloadLocationFlag] = useState(false);
  const router = useRouter();
  const pathName = usePathname();
  const theme = useTheme();
  /** Show logo from `sm` up; xs uses compact header / search-only layout */
  const showNavLogo = useMediaQuery(theme.breakpoints.up("sm"));
  const isSmallScreenSearchBar = useMediaQuery(theme.breakpoints.down("sm"));
  /** Home layout tweaks (full-bleed, flat top) apply on xs only — keeps sm+ / desktop topbar consistent */
  const isHomeMobileLayout =
    isHomeHubPath(pathName) && isSmallScreenSearchBar;

  const handleLoader = () => {
    setLoading(true);
  };
  const blankFunction = () => {};

  const handleLocationDialog = () => {
    setOpenDialog(true);
  };
  const closeDialog = () => {
    setOpenDialog(false);
  };

  const handleLogout = () => {
    if (!isHomeHubPath(pathName)) {
      handleLoader();
    } else {
      blankFunction();
    }
    localStorage.clear();
    Cookies.remove("isAlreadyLogin");
    Cookies.remove("serviceHub");
    Cookies.remove("serviceHubSlug");
    setAlreadyLogin(false);
    toast.success("You are logged out", {
      duration: 5000,
      style: {
        padding: "16px",
      },
    });
    router.push("/");
    setReloadLocationFlag(true);
    // window.location.reload();
  };

  function handleLoginClick() {
    setShowLoginBox(true);
  }
  function handleLoginClose() {
    setShowLoginBox(false);
  }
  function handleUserLoggedIn() {
    localStorage.setItem("isAlreadyLogin", "true");
    // Cookies.set("isAlreadyLogin", "true");
    Cookies.set("isAlreadyLogin", "true", {
      expires: 365,
      secure: true,
      sameSite: "Strict", // or 'Lax', depending on your use case
    });

    setAlreadyLogin(true);
  }

  useEffect(() => {
    const value = localStorage.getItem("isAlreadyLogin");
    if (value && value == "true") {
      setAlreadyLogin(true);
    }
  }, []);

  useEffect(() => {
    const addressRow = localStorage.getItem("address");
    if (addressRow == null || addressRow == "null") {
      // handleLocationDialog();
    } else {
      setAddress(JSON.parse(atob(localStorage.getItem("address") as string)));
    }
    setUpdateAddress(false);
  }, [updateAddress]);

  const handleAddressUpdate = useCallback(() => {
    setUpdateAddress(true);
  }, []);

  return (
    <>
      <LocationDialog
        openDialog={openDialog}
        setCloseDialog={closeDialog}
        handleAddressUpdate={handleAddressUpdate}
        reloadLocationFlag={reloadLocationFlag}
      />
      <Grid
        container
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        margin="auto"
        sx={{
          position: {
            xs: !visible ? "sticky" : reloadScrollFlag ? "sticky" : "inherit",
            sm: "sticky",
          },
          background: {
            xs:
              !isHomeHubPath(pathName)
                ? "rgba(255,255,255,0.92)"
                : reloadNavBackground === 0.1
                ? "rgba(255,255,255,0.92)"
                : "transparent",
            sm: "transparent",
          },
          zIndex: "1000",
          display: "block",
          top: 0,
          boxShadow: {
            xs:
              !isHomeHubPath(pathName)
                ? "none"
                : reloadNavBackground === 0.1
                ? "none"
                : "none",
            sm: "none",
          },
          paddingTop: {
            xs: "calc(env(safe-area-inset-top, 0px) + 0.2rem)",
            sm: "calc(env(safe-area-inset-top, 0px) + 0.75rem)",
          },
        }}
        className="navbar-scroller max-w-full min-w-0 overflow-x-hidden"
      >
        <Grid
          container
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          margin="auto"
          sx={{
            /* Mobile / tablet: full width; desktop (md+): 80% centered */
            width: { xs: "100%", md: "80%" },
            maxWidth: { xs: "100%", md: "80%" },
            mx: "auto",
            padding: {
              xs: isHomeMobileLayout ? "0" : "0px 0.5rem",
              sm: "0 1rem",
              md: 0,
              lg: 0,
            },
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            style={{ width: "100%" }}
          >
            <Box
              sx={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 100%)",
                backdropFilter: "blur(20px)",
                border: {
                  xs: isHomeMobileLayout
                    ? "none"
                    : "1px solid rgba(18,53,36,0.08)",
                  sm: "1px solid rgba(18,53,36,0.08)",
                },
                borderBottom: {
                  xs: isHomeMobileLayout
                    ? "1px solid rgba(18,53,36,0.08)"
                    : undefined,
                  sm: undefined,
                },
                borderRadius: {
                  xs: isHomeMobileLayout ? 0 : "18px",
                  sm: "24px",
                },
                boxShadow: {
                  xs: isHomeMobileLayout
                    ? "0 8px 24px rgba(18,53,36,0.12)"
                    : "0 16px 36px rgba(18,53,36,0.1)",
                  sm: "0 16px 36px rgba(18,53,36,0.1)",
                },
                height: "auto",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                padding: {
                  xs: isHomeMobileLayout ? "0.35rem 0 0" : "0.15rem 0.15rem",
                  sm: "0.3rem 0.45rem",
                },
                position: "relative",
                top: 0,
                left: 0,
                zIndex: 2,
              }}
            >
              <Box
                component="header"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "nowrap",
                  gap: { xs: 1, sm: 1.5, md: 2 },
                  width: "100%",
                  minHeight: { xs: 52, sm: 64, md: 72 },
                  maxWidth: "100%",
                  mx: "auto",
                  px: { xs: isHomeMobileLayout ? 1.25 : 0.25, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.75, md: 2 },
                    flex: "1 1 auto",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: showNavLogo ? "inline-block" : "none",
                      lineHeight: 0,
                      flexShrink: 0,
                    }}
                  >
                    <Link
                      prefetch={false}
                      href={"/"}
                      style={{ textDecoration: "none", color: "inherit" }}
                      onClick={
                        !isHomeHubPath(pathName) ? handleLoader : blankFunction
                      }
                    >
                      <Image
                        loading="lazy"
                        src={Logo}
                        alt="SalonTym"
                        height={500}
                        width={500}
                        style={{
                          width: "clamp(120px, 14vw, 168px)",
                          cursor: "pointer",
                          height: "auto",
                        }}
                      />
                    </Link>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      cursor: "pointer",
                      alignItems: "center",
                      padding: "0.5rem 0.65rem",
                      borderRadius: "14px",
                      background: "rgba(244,248,245,0.88)",
                      border: "1px solid rgba(18,53,36,0.08)",
                      minWidth: 0,
                      flex: { xs: "1 1 auto", md: "0 1 auto" },
                    }}
                    onClick={handleLocationDialog}
                  >
                    <LocationOnIcon
                      sx={{ color: "#1f7a4d", fontSize: 18, flexShrink: 0 }}
                    />
                    <Typography
                      title={address.formattedAddress}
                      sx={{
                        pl: 0.75,
                        pr: 0.25,
                        fontWeight: 600,
                        color: "#143125",
                        maxWidth: {
                          xs: "min(42vw, 11rem)",
                          sm: "10rem",
                          md: "14rem",
                          lg: "18rem",
                        },
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      }}
                    >
                      {address.formattedAddress}
                    </Typography>

                    <KeyboardArrowDownIcon
                      sx={{
                        color: "#15741a",
                        cursor: "pointer",
                        flexShrink: 0,
                        fontSize: 20,
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    flex: { xs: 0, sm: "1 1 auto" },
                    minWidth: { xs: 0, sm: 120, md: 200 },
                    maxWidth: { sm: "100%" },
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    px: { sm: 0.5, md: 1 },
                  }}
                >
                  <SearchBar setReloadCartService={setReloadCartService} />
                </Box>
                <LoginContainer sx={{ flexShrink: 0, ml: { xs: 0.5, md: 0 } }}>
                  {/* <CartIcon
                blankFunction={blankFunction}
                handleLoader={handleLoader}
              /> */}
                  {alreadyLogin ? (
                    <DropDown handleLogout={handleLogout} />
                  ) : (
                    <LoginButton
                      variant="contained"
                      color="success"
                      sx={{
                        padding: "9px 13px",
                        borderRadius: "999px",
                        marginLeft: 0,
                        background:
                          "linear-gradient(135deg, #123524 0%, #1f7a4d 100%)",
                        boxShadow: "0 12px 24px rgba(18,53,36,0.18)",
                        width: { xs: "78px", sm: "88px", md: "88px" },
                        height: { xs: "34px", sm: "38px", md: "38px" },
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #123524 0%, #1f7a4d 100%)",
                          transform: "translateY(-1px)",
                        },
                      }}
                      onClick={handleLoginClick}
                    >
                      <Typography variant="body2">Login&nbsp;</Typography>
                      <AccountCircleIcon sx={{ fontSize: "20px" }} />
                    </LoginButton>
                  )}
                  <Login
                    showLoginBox={showLoginBox}
                    handleLoginClose={handleLoginClose}
                    handleUserLoggedIn={handleUserLoggedIn}
                  />
                </LoginContainer>
              </Box>
              {isSmallScreenSearchBar ? (
                <Box
                  sx={{
                    /* Full-width search on mobile: no side inset */
                    width: { xs: "100%", sm: "100%" },
                    maxWidth: { xs: "100%", sm: "100%" },
                    boxSizing: "border-box",
                    px: { xs: 0, sm: 0 },
                    /* Break out of inner grid horizontal padding when present */
                    /* Inner grid 0.5rem + bar shell 0.15rem horizontal padding on xs */
                    ...(!isHomeMobileLayout
                      ? {
                          ml: { xs: "-0.65rem", sm: 0 },
                          mr: { xs: "-0.65rem", sm: 0 },
                          width: { xs: "calc(100% + 1.3rem)", sm: "100%" },
                          maxWidth: { xs: "none", sm: "100%" },
                        }
                      : {}),
                    pt: { xs: 0.5, sm: 0 },
                    pb: { xs: 0.75, sm: 0 },
                    mt: { xs: 0.25, sm: 0 },
                    bgcolor: {
                      xs: "rgba(248,251,249,0.98)",
                      sm: "rgba(255,255,255,0.98)",
                    },
                    borderTop: {
                      xs: "1px solid rgba(18,53,36,0.07)",
                      sm: "none",
                    },
                    borderRadius: { xs: 0, sm: 0 },
                    position: "relative",
                    zIndex: 1,
                    alignSelf: "stretch",
                    minWidth: 0,
                  }}
                >
                  <SearchBarMobileView />
                </Box>
              ) : null}
            </Box>
          </motion.div>
        </Grid>
      </Grid>
      {loading && <Loader />}
    </>
  );
};

export default Navbar;
